import type {
	AutoAssignResult,
	AutoAssignSuggestion,
	CreateTransfersBody,
	CreateTransfersResult,
	DismissSuggestionsResult,
	PatchTransportRequestBody,
	SetAllocationsBody,
	TransportAllocationOptions,
	TransportBulkStatusBody,
	TransportBulkStatusResult,
	TransportRequestRow,
	TransportRequestsPage,
	TransportRequestsQueryInput,
	TransportSuggestResult,
	TransportSuggestionDayCounts,
	TransportSuggestionsQueryInput,
	TransportSuggestionsResult,
	TransportActivityPage,
	TransportAllocationStepResult,
	TransportSummaryResult,
	TransportTripDetail,
} from "@seagames/contracts";
import { type InfiniteData, infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { invalidateLogistics } from "./logistics-cache";

/**
 * Transportation (M10): the dispatch desk's reads and writes (PLAN §3.1),
 * on the contract's wire shapes (`@seagames/contracts` transport.ts). Every
 * read is ONE request the API answers with one statement; nothing here loops
 * over rows. Keys live under `["transport", …]` (S5's fleet reads use
 * `["transport-fleet", …]`), so one invalidate refreshes every trip view —
 * the desk's, the NOC's and the provider's.
 */

/** Drop unset keys so equal filters make equal query keys. */
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;

/** `/transport/requests` filters in WIRE form (`status` is the comma list, booleans are "true"). */
export type TransportRequestsFilter = Omit<TransportRequestsQueryInput, "status" | "wheelchair" | "redispatch"> & {
	status?: string;
	wheelchair?: "true";
	redispatch?: "true";
};

export const transportKeys = {
	all: ["transport"] as const,
	summary: (gamesId: string, date: string | undefined) => ["transport", "summary", gamesId, date ?? null] as const,
	suggestions: (f: TransportSuggestionsQueryInput) => ["transport", "suggestions", clean(f)] as const,
	suggestionDayCounts: (gamesId: string, from: string, to: string) => ["transport", "suggestions", "day-counts", gamesId, from, to] as const,
	requests: (f: TransportRequestsFilter) => ["transport", "requests", clean(f)] as const,
	request: (id: string) => ["transport", "request", id] as const,
	activity: (id: string) => ["transport", "request", id, "activity"] as const,
	options: (id: string) => ["transport", "request", id, "options"] as const,
};

/**
 * `GET /transport/summary` — the Overview strip and Needs attention: scalar
 * subqueries in one statement. `date` defaults to today (games' zone). The
 * shape follows the caller: a NOC gets `TransportNocSummary` (`view: "noc"`),
 * everyone else `TransportSummary` — read it with the `lib/transport/view`
 * helpers (`deskSummary`, `nocSummary`, `statusCount`, …).
 */
export const transportSummaryOptions = (gamesId: string, date?: string) =>
	queryOptions({
		queryKey: transportKeys.summary(gamesId, date),
		queryFn: ({ signal }) => api.get<TransportSummaryResult>("/transport/summary", { query: clean({ games_id: gamesId, date }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** `GET /transport/suggestions` — computed, never stored (X1); ≤ 3 days, ≤ 500 groups (`truncated`). */
export const transportSuggestionsOptions = (f: TransportSuggestionsQueryInput) =>
	queryOptions({
		queryKey: transportKeys.suggestions(f),
		queryFn: ({ signal }) => api.get<TransportSuggestionsResult>("/transport/suggestions", { query: { ...clean(f) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/**
 * `GET /transport/suggestions/day-counts` — people waiting for a transfer per
 * day and direction (the day chips' badges, and "Next: … waiting"). Keyed by
 * the chips' visible WEEK, never the picked day: clicking a day inside it
 * never refetches. Dispatch only.
 */
export const transportSuggestionDayCountsOptions = (gamesId: string, from: string, to: string) =>
	queryOptions({
		queryKey: transportKeys.suggestionDayCounts(gamesId, from, to),
		queryFn: ({ signal }) => api.get<TransportSuggestionDayCounts>("/transport/suggestions/day-counts", { query: { games_id: gamesId, from, to }, signal }),
		// Looked up by date, so the previous window's rows never land on the wrong chip.
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** `GET /transport/requests` — keyset; the total rides on the first page (a scalar count in the same statement). The row shape follows the caller (`view`). */
export const transportRequestsOptions = (f: TransportRequestsFilter) =>
	queryOptions({
		queryKey: transportKeys.requests(f),
		queryFn: ({ signal }) => api.get<TransportRequestsPage>("/transport/requests", { query: { ...clean(f) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});

/**
 * The same list for the dispatch desk, typed to its view: an admin or a
 * dispatch group always gets `view: "full"` rows (the API picks the view by
 * who asks), so the page's type is the full row. Its own key (`desk`), so
 * the narrowed page never shares a cache entry with a NOC's.
 */
export const deskRequestsOptions = (f: TransportRequestsFilter) =>
	queryOptions({
		queryKey: [...transportKeys.requests(f), "desk"] as const,
		queryFn: async ({ signal }): Promise<DeskRequestsPage> => {
			const page = await api.get<TransportRequestsPage>("/transport/requests", { query: { ...clean(f) }, signal });
			return { ...page, items: page.items.filter((r): r is TransportRequestRow => r.view === "full") };
		},
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});

export type DeskRequestsPage = Omit<TransportRequestsPage, "items"> & { items: TransportRequestRow[] };

/** `GET /transport/requests/:id` — request + places + passengers + allocations + actions + checks, one query. */
export const transportRequestOptions = (id: string) =>
	queryOptions({
		queryKey: transportKeys.request(id),
		queryFn: ({ signal }) => api.get<TransportTripDetail>(`/transport/requests/${id}`, { signal }),
		staleTime: 10_000,
	});

/** `GET /transport/requests/:id/activity` — ONE item per write (the request, its passengers and allocations), newest first, keyset. */
export const transportActivityOptions = (id: string) =>
	infiniteQueryOptions<TransportActivityPage, Error, InfiniteData<TransportActivityPage>, ReturnType<typeof transportKeys.activity>, string | undefined>({
		queryKey: transportKeys.activity(id),
		queryFn: ({ signal, pageParam }) => api.get<TransportActivityPage>(`/transport/requests/${id}/activity`, { query: { cursor: pageParam }, signal }),
		initialPageParam: undefined,
		getNextPageParam: (last) => last.next_cursor ?? undefined,
		staleTime: 15_000,
	});

/** `GET /transport/requests/:id/options` — vehicles (dispatch cluster first) and drivers with their flags, one statement per list. */
export const allocationOptionsOptions = (id: string, enabled = true) =>
	queryOptions({
		queryKey: transportKeys.options(id),
		queryFn: ({ signal }) => api.get<TransportAllocationOptions>(`/transport/requests/${id}/options`, { signal }),
		staleTime: 10_000,
		enabled,
	});

/**
 * After a transport write: every trip read (summary, lists, suggestions, the open request and its
 * options) and the rest of the travel chain (`invalidateLogistics`): a transfer or a driver changes
 * A&D's readiness, the Contingent Dashboard and the record's logistics chain.
 */
export const invalidateTransport = invalidateLogistics;

// ── Writes ──────────────────────────────────────────────────────────────────

/** `POST /transport/transfers` — "Create N transfers" / "Merge into one". Skipped legs come back, never a throw (one empty group → 409 TRANSFER_EXISTS). */
export async function createTransfers(body: CreateTransfersBody): Promise<CreateTransfersResult> {
	const res = await api.post<CreateTransfersResult>("/transport/transfers", body);
	// The legs left the suggestions; the A&D page and the records show their transfer.
	await invalidateTransport();
	return res;
}

/** `POST /transport/suggestions/dismiss` — the legs' `needs_transport` off (one UPDATE). */
export async function dismissSuggestions(gamesId: string, legIds: string[]): Promise<DismissSuggestionsResult> {
	const res = await api.post<DismissSuggestionsResult>("/transport/suggestions/dismiss", { games_id: gamesId, leg_ids: legIds });
	await invalidateTransport();
	return res;
}

/** `GET /transport/requests/:id/suggest` — her greedy pick, computed; writes nothing. */
export const suggestAllocations = (id: string) => api.get<TransportSuggestResult>(`/transport/requests/${id}/suggest`);

/** `PUT /transport/requests/:id/allocations` — replace the vehicles; 422 ALLOCATION_INVALID carries the check (`details`). */
export const saveAllocations = (id: string, body: SetAllocationsBody) => api.put<TransportTripDetail>(`/transport/requests/${id}/allocations`, body);

/** `POST /transport/auto-assign/suggest` — the vehicles and drivers the system would pick, in pickup order; nothing is saved. */
export const suggestAutoAssign = (requestIds: string[], acceptPartial: boolean) =>
	api.post<AutoAssignSuggestion>("/transport/auto-assign/suggest", { request_ids: requestIds, accept_partial: acceptPartial });

/** `POST /transport/auto-assign` — the reviewed suggestion; `changed` = the picks moved, nothing saved. */
export const autoAssign = (requestIds: string[], acceptPartial: boolean, fingerprint: string) =>
	api.post<AutoAssignResult>("/transport/auto-assign", { request_ids: requestIds, accept_partial: acceptPartial, fingerprint });

/** `POST /transport/requests/bulk/status` — cancel / reject with a reason; anything left alone is listed. */
export const bulkTripStatus = (body: TransportBulkStatusBody) => api.post<TransportBulkStatusResult>("/transport/requests/bulk/status", body);

/** `PATCH /transport/requests/:id` — pickup / drive / cluster; a fixed pickup stops following travel, `follows_travel: true` ties it back. */
export const patchTrip = (id: string, body: PatchTransportRequestBody) => api.patch<TransportTripDetail>(`/transport/requests/${id}`, body);

/** `POST /transport/allocations/:id/start|arrive` — the vehicle left / arrived. */
export const stepAllocation = (allocationId: string, step: "start" | "arrive") => api.post<TransportAllocationStepResult>(`/transport/allocations/${allocationId}/${step}`);
